import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { Session } from '@providers/session/session.service';
import {
  WearablesIntegrationCategoriesParams,
  WearablesIntegrationCategoriesResponse,
  WearablesIntegrationSyncHealthAppProgressParams,
  WearablesIntegrationSyncHealthAppProgressResponse,
  WearablesIntegrationSyncHealthAppReceivePayload,
  WearablesIntegrationSyncHealthAppReceiveResponse,
  WearablesIntegrationUserConnectWearablePayload,
  WearablesIntegrationUserConnectWearableResponse,
  WearablesIntegrationUserWearableCategoriesParams,
  WearablesIntegrationUserWearableCategoriesPayload,
  WearablesIntegrationUserWearableCategoriesPostResponse,
  WearablesIntegrationUserWearableCategoriesResponse,
  WearablesIntegrationUserWearableParams,
  WearablesIntegrationUserWearablePayload,
  WearablesIntegrationUserWearablePostResponse,
  WearablesIntegrationUserWearableResponse,
  WearablesIntegrationUserWearableSuggestionsParams,
  WearablesIntegrationUserWearableSuggestionsPayload,
  WearablesIntegrationUserWearableSuggestionsPostResponse,
  WearablesIntegrationUserWearableSuggestionsResponse,
  WearablesIntegrationWearablesParams,
  WearablesIntegrationWearablesResponse
} from './wearables-integration.interface';

/**
 * @description Service to handle wearables integration.
 * @exports
 * @class WearablesIntegrationService
 */
@Injectable()
export class WearablesIntegrationService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(Session);

  private readonly paths = signal<{
    wearables: string;
    categories: string;
    userWearable: string;
    userWearableCategories: string;
    userWearableSuggestions: string;
    connectWearable: string;
    disconnectWearable: string;
    syncHealthAppProgress: string;
    syncHealthAppReceive: string;
  }>({
    wearables: '/wearables',
    categories: '/categories',
    userWearable: '/user/wearables',
    userWearableCategories: '/user/wearable/categories',
    userWearableSuggestions: '/user/wearable/suggestions',
    connectWearable: '/user/wearables/auth/connect',
    disconnectWearable: '/user/wearables/auth/disconnect',
    syncHealthAppProgress: '/sync/health-app/progress',
    syncHealthAppReceive: '/sync/health-app/receive'
  });
  private readonly servicePath = signal<string>('wearables-integration');

  /**
   * Wearables Integration.
   * Generic GET method.
   *
   * @template T Type of response.
   * @param {string} endpoint Endpoint.
   * @param {object} [params] Optional params.
   * @returns {Observable<T>}
   * @memberof WearablesIntegrationService
   */
  protected get<T>(
    endpoint: string,
    params?: Record<string, any>
  ): Observable<T> {
    const url = `${environment.API_V2_URL}/${this.servicePath()}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
    let httpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== null && params[key] !== undefined) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }

    return this.http.get<T>(url, {
      headers: this.getHttpHeaders(),
      params: httpParams
    });
  }

  /**
   * Wearables Integration.
   * Generic POST method.
   *
   * @template T Type of response.
   * @param {string} endpoint Endpoint.
   * @param {unknown} body Body.
   * @returns {Observable<T>}
   * @memberof WearablesIntegrationService
   */
  protected post<T>(endpoint: string, body: unknown): Observable<T> {
    const url = `${environment.API_V2_URL}/${this.servicePath()}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
    return this.http.post<T>(url, body, {
      headers: this.getHttpHeaders()
    });
  }

  /**
   * @description Connect Wearable.
   * @param {WearablesIntegrationUserConnectWearablePayload} [payload] Payload.
   * @returns {Observable<WearablesIntegrationUserConnectWearableResponse>}
   * @memberof WearablesIntegrationService
   */
  connectWearable(
    payload: WearablesIntegrationUserConnectWearablePayload
  ): Observable<WearablesIntegrationUserConnectWearableResponse> {
    return this.post<WearablesIntegrationUserConnectWearableResponse>(
      this.paths().connectWearable,
      payload
    );
  }

  /**
   * @description Disconnect Wearable.
   * @param {WearablesIntegrationUserConnectWearablePayload} [payload] Payload.
   * @returns {Observable<WearablesIntegrationUserConnectWearableResponse>}
   * @memberof WearablesIntegrationService
   */
  disconnectWearable(
    payload: WearablesIntegrationUserConnectWearablePayload
  ): Observable<WearablesIntegrationUserConnectWearableResponse> {
    return this.post<WearablesIntegrationUserConnectWearableResponse>(
      this.paths().disconnectWearable,
      payload
    );
  }

  /**
   * @description Get Categories.
   * @param {WearablesIntegrationCategoriesParams} [params] Query params.
   * @returns {Observable<WearablesIntegrationCategoriesResponse>}
   * @memberof WearablesIntegrationService
   */
  getCategories(
    params: WearablesIntegrationCategoriesParams
  ): Observable<WearablesIntegrationCategoriesResponse> {
    return this.get<WearablesIntegrationCategoriesResponse>(
      this.paths().categories,
      params
    );
  }

  /**
   * Get HTTP Headers.
   *
   * @returns {{ Authorization: string } | undefined }
   */
  getHttpHeaders(): { Authorization: string } | undefined {
    if (!this.session.token) {
      return undefined;
    }

    return {
      Authorization: `Bearer ${this.session.token}`
    };
  }

  /**
   * @description Get Sync Health App Progress.
   * @param {WearablesIntegrationSyncHealthAppProgressParams} params Query params.
   * @returns {Observable<WearablesIntegrationSyncHealthAppProgressResponse>}
   * @memberof WearablesIntegrationService
   */
  getSyncHealthAppProgress(
    params: WearablesIntegrationSyncHealthAppProgressParams
  ): Observable<WearablesIntegrationSyncHealthAppProgressResponse> {
    return this.get<WearablesIntegrationSyncHealthAppProgressResponse>(
      this.paths().syncHealthAppProgress,
      params
    );
  }

  /**
   * @description Get User Wearables.
   * @param {WearablesIntegrationUserWearableParams} [params] Query params.
   * @returns {Observable<WearablesIntegrationUserWearableResponse>}
   * @memberof WearablesIntegrationService
   */
  getUserWearable(
    params?: WearablesIntegrationUserWearableParams
  ): Observable<WearablesIntegrationUserWearableResponse> {
    return this.get<WearablesIntegrationUserWearableResponse>(
      this.paths().userWearable,
      params
    );
  }

  /**
   * @description Get User Wearable Categories.
   * @param {WearablesIntegrationUserWearableCategoriesParams} [params] Query params.
   * @returns {WearablesIntegrationUserWearableCategoriesResponse}
   * @memberof WearablesIntegrationService
   */
  getUserWearableCategories(
    params?: WearablesIntegrationUserWearableCategoriesParams
  ): Observable<WearablesIntegrationUserWearableCategoriesResponse> {
    return this.get<WearablesIntegrationUserWearableCategoriesResponse>(
      this.paths().userWearableCategories,
      params
    );
  }

  /**
   * @description Get Wearable Suggestions.
   * @param {WearablesIntegrationUserWearableSuggestionsParams} [params] Query params.
   * @returns {Observable<WearablesIntegrationUserWearableSuggestionsResponse>}
   * @memberof WearablesIntegrationService
   */
  getUserWearableSuggestions(
    params: WearablesIntegrationUserWearableSuggestionsParams
  ): Observable<WearablesIntegrationUserWearableSuggestionsResponse> {
    return this.get<WearablesIntegrationUserWearableSuggestionsResponse>(
      this.paths().userWearableSuggestions,
      params
    );
  }

  /**
   * @description Get Wearables.
   * @param {WearablesIntegrationWearablesParams} [params] Query params.
   * @returns {Observable<WearablesIntegrationWearablesResponse>}
   * @memberof WearablesIntegrationService
   */
  getWearables(
    params?: WearablesIntegrationWearablesParams
  ): Observable<WearablesIntegrationWearablesResponse> {
    return this.get<WearablesIntegrationWearablesResponse>(
      this.paths().wearables,
      params
    );
  }

  /**
   * @description Post Sync Health App Receive.
   * @param {WearablesIntegrationSyncHealthAppReceivePayload} payload Payload.
   * @returns {Observable<WearablesIntegrationSyncHealthAppReceiveResponse>}
   * @memberof WearablesIntegrationService
   */
  postSyncHealthAppReceive(
    payload: WearablesIntegrationSyncHealthAppReceivePayload
  ): Observable<WearablesIntegrationSyncHealthAppReceiveResponse> {
    return this.post<WearablesIntegrationSyncHealthAppReceiveResponse>(
      this.paths().syncHealthAppReceive,
      payload
    );
  }

  /**
   * @description Post User Wearable.
   * @param {WearablesIntegrationUserWearablePayload} [payload] Payload.
   * @returns {Observable<WearablesIntegrationUserWearablePostResponse>}
   * @memberof WearablesIntegrationService
   */
  postUserWearable(
    payload: WearablesIntegrationUserWearablePayload
  ): Observable<WearablesIntegrationUserWearablePostResponse> {
    return this.post<WearablesIntegrationUserWearablePostResponse>(
      this.paths().userWearable,
      payload
    );
  }

  /**
   * @description Post User Wearable Categories.
   * @param {WearablesIntegrationUserWearableCategoriesPayload} [payload] Payload.
   * @returns {Observable<WearablesIntegrationUserWearableCategoriesPostResponse>}
   * @memberof WearablesIntegrationService
   */
  postUserWearableCategories(
    payload: WearablesIntegrationUserWearableCategoriesPayload
  ): Observable<WearablesIntegrationUserWearableCategoriesPostResponse> {
    return this.post<WearablesIntegrationUserWearableCategoriesPostResponse>(
      this.paths().userWearableCategories,
      payload
    );
  }

  /**
   * @description Post Wearable Suggestions.
   * @param {WearablesIntegrationUserWearableSuggestionsPayload} [payload] Payload.
   * @returns {Observable<WearablesIntegrationUserWearableSuggestionsPostResponse>}
   * @memberof WearablesIntegrationService
   */
  postUserWearableSuggestions(
    payload: WearablesIntegrationUserWearableSuggestionsPayload
  ): Observable<WearablesIntegrationUserWearableSuggestionsPostResponse> {
    return this.post<WearablesIntegrationUserWearableSuggestionsPostResponse>(
      this.paths().userWearableSuggestions,
      payload
    );
  }
}
